import { useEffect, useState } from 'react';
import { onSnapshot } from 'firebase/firestore';
import { settingsRef } from '../lib/db';
import { useActiveTenantId } from '../lib/tenant';
import { DEFAULT_SETTINGS, normalizeSettings, type AppSettings } from '../types';

/** Live tenant-scoped settings doc (tenants/{tenantId}/settings/app — see
 *  settingsRef() in ../lib/db), falling back to DEFAULT_SETTINGS until it
 *  loads or if it doesn't exist yet. Normalizes the legacy string-list
 *  shape.
 *
 *  Phase 5D: the effect depends on useActiveTenantId() — without it, this
 *  subscribed once (empty deps) to whichever tenant was active at mount
 *  and never noticed switchActiveTenant reordering tenantIds, since
 *  switching is a live in-place update with no page reload/remount. Every
 *  consumer (settings.districts, .committees, .positions, …) would then
 *  silently keep showing the PREVIOUS tenant's settings after a switch —
 *  e.g. the dashboard's district-comparison heatmap mapping the new
 *  tenant's districtSummaries against the old tenant's district id list,
 *  finding no match, and showing "—" everywhere despite other
 *  tenant-scoped data (which already re-subscribed correctly) showing the
 *  real numbers. */
export function useSettings(): { settings: AppSettings; loading: boolean } {
  const [settings, setSettings] = useState<AppSettings>(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const activeTenantId = useActiveTenantId();

  useEffect(() => {
    setLoading(true);
    return onSnapshot(
      settingsRef(),
      (snap) => {
        if (snap.exists()) setSettings(normalizeSettings(snap.data()));
        setLoading(false);
      },
      () => setLoading(false),
    );
  }, [activeTenantId]);

  return { settings, loading };
}
